import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { FaRedo, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../Api";
import ScanSummary from "./ScanSummary";
import NotScannedDialog from "./NotScannedDialog";
import { getAppDateString } from "../utils/date";

export default function CancelPacketsPage() {
  const [selectedDate, setSelectedDate] = useState(getAppDateString());
  const [nsOpen, setNsOpen] = useState(false);
  const [nsGroup, setNsGroup] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const filteredTotal = useMemo(() => rows.length, [rows]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/cancel-scans", {
        params: {
          date: selectedDate,
          search: search.trim() || undefined,
        },
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch cancel packet scans:", err);
      setError(err.response?.data?.error || "Failed to load cancel packet scans.");
    } finally {
      setLoading(false);
    }
  }, [search, selectedDate]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const deleteRow = async (row) => {
    const ok = window.confirm(`Delete cancel packet scan "${row.tracking_id}"?`);
    if (!ok) return;

    try {
      await api.delete(`/api/cancel-scans/${row.id}`);
      toast.success("Cancel packet scan deleted");
      fetchRows();
    } catch (err) {
      console.error("Failed to delete cancel packet scan:", err);
      toast.error(err.response?.data?.message || "Failed to delete cancel packet scan");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Toolbar disableGutters sx={{ gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Cancel Packet Scans
          </Typography>
          <Typography color="text.secondary">
            {filteredTotal} scan{filteredTotal === 1 ? "" : "s"} on selected date
          </Typography>
        </Box>

        <TextField
          label="Date"
          type="date"
          size="small"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 170 }}
        />

        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") fetchRows();
          }}
          sx={{ width: { xs: "100%", sm: 260 } }}
        />

        <Button
          variant="contained"
          startIcon={<FaRedo />}
          onClick={fetchRows}
          disabled={loading}
        >
          Refresh
        </Button>
      </Toolbar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
              <TableCell>Scanned On</TableCell>
              <TableCell>Tracking ID</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  {loading ? "Loading..." : "No cancel packet scans found."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.scanned_on || "-"}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{row.tracking_id}</TableCell>
                  <TableCell>{row.account_name || "-"}</TableCell>
                  <TableCell>{row.order_id || "-"}</TableCell>
                  <TableCell>{row.sku || "-"}</TableCell>
                  <TableCell align="right">{row.quantity ?? "-"}</TableCell>
                  <TableCell>{row.source || "web"}</TableCell>
                  <TableCell align="center">
                    <IconButton color="error" size="small" onClick={() => deleteRow(row)}>
                      <FaTrashAlt />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* the day's scan position, as on the other pages */}
      <ScanSummary
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        readOnlyDate={true}
        onOpenNotScanned={(g) => { setNsGroup(g || ""); setNsOpen(true); }}
      />
      {nsOpen && (
        <NotScannedDialog open={nsOpen} onClose={() => setNsOpen(false)}
                          accounts={[]} initialGroup={nsGroup} />
      )}
    </Box>
  );
}
